/**
 * Country Configuration System
 * 
 * Future-proof configuration for different countries/regions.
 * Each country has associated defaults for:
 * - Currency (code + symbol)
 * - Timezone(s)
 * - Tax settings
 * - Locale/formatting preferences
 * 
 * To add a new country:
 * 1. Add the country config below
 * 2. The rest of the app will automatically pick up the defaults
 */

export const COUNTRIES = {
  USA: {
    code: 'USA',
    name: 'United States',
    flag: '🇺🇸',
    currency: {
      code: 'USD',
      symbol: '$',
      symbolPosition: 'before', // $100
      decimalPlaces: 2
    },
    timezones: [
      { value: 'America/New_York', label: 'Eastern (ET)' },
      { value: 'America/Chicago', label: 'Central (CT)' },
      { value: 'America/Denver', label: 'Mountain (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
      { value: 'America/Anchorage', label: 'Alaska (AKT)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' }
    ],
    defaultTimezone: 'America/Chicago',
    tax: {
      enabled: false, // Varies by state - user must configure
      defaultRate: null, // No default - varies by state/city
      label: 'Tax',
      rateVariesByRegion: true
    },
    dateFormat: 'MM/DD/YYYY',
    phoneFormat: '+1 (XXX) XXX-XXXX'
  },
  
  UAE: {
    code: 'UAE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: {
      code: 'AED',
      symbol: 'AED',
      symbolPosition: 'before', // AED 100
      decimalPlaces: 2
    },
    timezones: [
      { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' }
    ],
    defaultTimezone: 'Asia/Dubai',
    tax: {
      enabled: true,
      defaultRate: 5, // UAE VAT is 5%
      label: 'Tax',
      rateVariesByRegion: false
    },
    dateFormat: 'DD/MM/YYYY',
    phoneFormat: '+971 XX XXX XXXX'
  }
  
  // Future countries (templates):
  // UK: { code: 'UK', currency: { code: 'GBP', symbol: '£' }, tax: { defaultRate: 20 } }
  // EU: { code: 'EU', currency: { code: 'EUR', symbol: '€' }, tax: { defaultRate: varies } }
  // Canada: { code: 'CAN', currency: { code: 'CAD', symbol: 'C$' }, tax: { defaultRate: varies } }
  // Australia: { code: 'AUS', currency: { code: 'AUD', symbol: 'A$' }, tax: { defaultRate: 10 } }
};

// Helper functions
export const getCountryConfig = (countryCode) => {
  return COUNTRIES[countryCode] || COUNTRIES.USA; // Default to USA
};

export const getCountryCurrency = (countryCode) => {
  const config = getCountryConfig(countryCode);
  return config.currency;
};

export const getCountryTimezones = (countryCode) => {
  const config = getCountryConfig(countryCode);
  return config.timezones;
};

export const getCountryTaxSettings = (countryCode) => {
  const config = getCountryConfig(countryCode);
  return config.tax;
};

export const formatCurrencyByCountry = (amount, countryCode) => {
  const currency = getCountryCurrency(countryCode);
  const formattedAmount = amount.toLocaleString(undefined, {
    minimumFractionDigits: currency.decimalPlaces,
    maximumFractionDigits: currency.decimalPlaces
  });
  
  if (currency.symbolPosition === 'before') {
    return `${currency.symbol}${formattedAmount}`;
  }
  return `${formattedAmount} ${currency.symbol}`;
};

export const getCountryOptions = () => {
  return Object.values(COUNTRIES).map(country => ({
    value: country.code,
    label: `${country.flag} ${country.name} (${country.currency.code})`,
    shortLabel: `${country.flag} ${country.code}`
  }));
};

export const getCurrencyOptions = () => {
  const seen = new Set();
  return Object.values(COUNTRIES)
    .filter(country => {
      if (seen.has(country.currency.code)) return false;
      seen.add(country.currency.code);
      return true;
    })
    .map(country => ({
      value: country.currency.code,
      label: `${country.currency.symbol} ${country.currency.code}`,
      symbol: country.currency.symbol
    }));
};

export default COUNTRIES;
